import { AuthRequest } from "@core/requests/auth.request";
import { AuthenticationService } from "@core/services/authentication.service";

import {
  catchError,
  Observable,
  Subject,
  switchMap,
  take,
  throwError,
} from "rxjs";

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from "@angular/common/http";
import { Injectable } from "@angular/core";

const REFRESH_TOKEN_PATH = "/authenticate/refresh-token";

@Injectable()
export class AutorizationInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshedToken$ = new Subject<string>();

  constructor(
    private authenticationService: AuthenticationService,
    private authenticationRequest: AuthRequest,
  ) {}

  whitelist = ["viacep.com.br"];

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const isWhitelisted = this.whitelist.some((url) =>
      request.url.includes(url),
    );

    if (isWhitelisted) return next.handle(request);

    const credential = this.authenticationService.getCredential();

    if (!credential?.accessToken) return next.handle(request);

    const interceptedRequest = this.setTokenInRequest(
      credential.accessToken,
      request,
    );

    return next.handle(interceptedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        return this.handleUnauthorized(request, next, error);
      }),
    );
  }

  setTokenInRequest(
    token: string,
    request: HttpRequest<unknown>,
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { authorization: `Bearer ${token}` },
    });
  }

  private handleUnauthorized(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    if (request.url.includes(REFRESH_TOKEN_PATH)) {
      return this.endSession(error);
    }

    if (this.isRefreshing) {
      return this.refreshedToken$.pipe(
        take(1),
        switchMap((token) =>
          next.handle(this.setTokenInRequest(token, request)),
        ),
      );
    }

    return this.refreshSession(request, next, error);
  }

  private refreshSession(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    const refreshToken =
      this.authenticationService.getCredential()?.refreshToken;

    if (!refreshToken) return this.endSession(error);

    this.isRefreshing = true;
    this.refreshedToken$ = new Subject<string>();

    return this.authenticationRequest.refreshToken(refreshToken).pipe(
      switchMap((credential) => {
        this.authenticationService.setCredential(credential);
        this.isRefreshing = false;
        this.refreshedToken$.next(credential.accessToken);

        return next.handle(
          this.setTokenInRequest(credential.accessToken, request),
        );
      }),
      catchError((refreshError: HttpErrorResponse) => {
        this.isRefreshing = false;
        this.refreshedToken$.error(refreshError);

        return this.endSession(refreshError);
      }),
    );
  }

  private endSession(error: HttpErrorResponse): Observable<never> {
    this.authenticationService.logoff();

    return throwError(() => error);
  }
}
